import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import SignalKPluginManager from './SignalKPluginManager';
import TripPreferences from './TripPreferences';
import SignalKNotificationSettings from './SignalKNotificationSettings';
import LocationPreferences from '../friends/LocationPreferences';
import McpConnector from './McpConnector';
import {
  ServerIcon,
  UserIcon,
  MapPinIcon,
  BellIcon,
  UserGroupIcon,
  HeartIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';
import ProfileSettings from './profile/ProfileSettings';
import HeartbeatSettings from './HeartbeatSettings';

const SettingsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const SettingsHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.h1};
  font-weight: ${theme.fontWeights.bold};
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.lg};
`;

const TabNavigation = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid ${theme.colors.border.secondary};
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  color: ${props => props.active ? theme.colors.primary.main : theme.colors.text.secondary};
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.active ? theme.colors.primary.main : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    color: ${theme.colors.primary.main};
  }
`;

const TabContent = styled.div`
  min-height: 400px;
`;

const PlaceholderContent = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: 3rem;
  text-align: center;
  border: 1px solid ${theme.colors.border.primary};
`;

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    {
      id: 'profile',
      name: 'Profile',
      icon: UserIcon,
      description: 'User account settings'
    },
    {
      id: 'location-sharing',
      name: 'Location Sharing',
      icon: UserGroupIcon,
      description: 'Control location sharing with friends and proximity alerts'
    },
    {
      id: 'signalk',
      name: 'SignalK Plugin',
      icon: ServerIcon,
      description: 'Manage SignalK plugin installations and tokens'
    },
    {
      id: 'signalk-notifications',
      name: 'SignalK Alerts',
      icon: BellIcon,
      description: 'Manage live feed connection notifications'
    },
    {
      id: 'trips',
      name: 'Trip Preferences',
      icon: MapPinIcon,
      description: 'Configure trip tracking, units, and notifications'
    },
    {
      id: 'heartbeat',
      name: 'Boat Status',
      icon: HeartIcon,
      description: 'AI heartbeat checks and email alerts'
    },
    {
      id: 'mcp',
      name: 'AI Connector',
      icon: CpuChipIcon,
      description: 'Connect Claude/ChatGPT to your BoatWise data via MCP'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSettings />;
      case 'location-sharing':
        return <LocationPreferences />;
      case 'signalk':
        return <SignalKPluginManager />;
      case 'signalk-notifications':
        return <SignalKNotificationSettings />;
      case 'trips':
        return <TripPreferences />;
      case 'heartbeat':
        return <HeartbeatSettings />;
      case 'mcp':
        return <McpConnector />;
      default:
        return null;
    }
  };

  return (
    <SettingsContainer>
      <SettingsHeader>
        <Title>Settings</Title>
        <Subtitle>
          Manage your BoatWise configuration and preferences
          <span style={{ marginLeft: '0.5rem', fontSize: '0.85em', color: theme.colors.text.secondary }}>
            build {process.env.REACT_APP_BUILD_ID || 'dev'}
          </span>
        </Subtitle>
      </SettingsHeader>

      <TabNavigation>
        {tabs.map((tab) => {
          const IconComponent = tab.icon;
          return (
            <Tab
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <IconComponent className="w-5 h-5" />
              {tab.name}
            </Tab>
          );
        })}
      </TabNavigation>

      <TabContent>
        {renderTabContent()}
      </TabContent>
    </SettingsContainer>
  );
};

export default Settings;